import * as React from "react";

import { AjaxManager } from "@impero-com/common/utils/ajax";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever, set } from "@impero-com/common/utils/obj";
import memoizeOne from "memoize-one";
import moment, { Moment } from "moment-timezone";

import {
    SubsequentStepIndex,
    DefaultOrCustomForDueDateOffsetForMonthOffsetPeriod as DefaultOrCustomOffset,
    OffsetType,
    OffsetAnchor,
    DayOfWeek,
    Month,
    TwoMonths,
    Quarter,
    HalfYear,
    StepAllocatedTime,
    SchedulingType,
    Presence,
    StepIndex,
    UpdatableControlScheduling,
    UpdatableControlRecurrenceRule,
    SchedulingOccurrenceChangeForDueDateOffsetForMonthOffsetPeriod as SchedulingOccurrenceChange,
    WeeklyRecurrenceRule,
    RecurrenceRuleWithOffsetForMonthAndDueDateOffsetForMonthOffsetPeriod as MonthlyRecurrenceRule,
    RecurrenceRuleWithOffsetForTwoMonthsAndDueDateOffsetForMonthOffsetPeriod as EveryTwoMonthsRecurrenceRule,
    RecurrenceRuleWithOffsetAndFinancialYearForQuarterAndDueDateOffsetForMonthOffsetPeriod as QuarterlyRecurrenceRule,
    RecurrenceRuleWithOffsetAndFinancialYearForHalfYearAndDueDateOffsetForMonthOffsetPeriod as HalfYearlyRecurrenceRule,
    YearlyRecurrenceRule,
    ControlCoveredPeriod,
    ControlScheduling,
    FinancialYearView,
    CalendarView,
    UserTagOverviewListDto,
} from "api/generated/model";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import {
    RawEditableDatePicker,
    RawReadOnlyDatePicker,
} from "components/form/legacyFields/multiDatePicker";
import {
    SelectInput,
    SelectInputProps,
    SelectInputCommonProps,
} from "components/form/legacyFields/selectInput";
import { Time, timeFromString, timeToString } from "components/timePicker";
import { ReviewerOffsetUnit } from "legacyTypes/control";
import { MICROSECONDS_IN_AN_HOUR } from "org/control/view/utils/microsecondsToTime";
import { COVERED_PERIOD_VALUES, CoveredPeriodKey } from "utils/coveredPeriod";
import { Guid } from "utils/guid";
import { isNotUndefined } from "utils/typePredicates";

import { ErrorMap, ErrorValue } from "./common/errors";
import {
    MultiDefaultDueDateOffsetInput,
    MultiDefaultDueDateOffsetValues,
    PeriodType,
} from "./common/multiDefaultDueDateOffsetInput";
import {
    MultiDueTimeAndTimezoneInput,
    DueTimeOrTimezoneChange,
} from "./common/multiDueTimeAndTimezoneInput";
import { MultiResponsibleAllocatedTimeInput } from "./common/multiResponsibleAllocatedTimeInput";
import {
    MultiReviewerAllocatedTimeInput,
    ReviewerAllocatedTime,
} from "./common/multiReviewerAllocatedTimeInput";
import { MultiPeriodInput } from "./common/periodWidget";
import { RemindersBody } from "./common/remindersWidget";
import {
    EveryTwoMonthsRecurrenceRuleWithArrays,
    GenericOccurrencesArray,
    HalfYearlyRecurrenceRuleWithArrays,
    MonthDueDateOffsetWithArrays,
    MonthlyRecurrenceRuleWithArrays,
    QuarterlyRecurrenceRuleWithArrays,
    RecurrenceRuleWithArrays,
    YearlyRecurrenceRuleWithArrays,
} from "./common/rulesWithArrays";
import { StartingFromDatePicker, translateLabel } from "./common/startingFrom";
import type { StartDateLabelType } from "./common/startingFrom";
import { ErrorKey, WithArrays } from "./common/utils";
import { HalfYearlyCustomDueDatesModal } from "./halfYearly/halfYearlyCustomDueDatesModal";
import {
    FullHalfYearlyRecurrenceRuleUpdater,
    PartialHalfYearlyRecurrenceRuleUpdater,
    PartialHalfYearlyRecurrenceRuleViewer,
} from "./halfYearly/modifyHalfYearlySchedulingBody";
import {
    ManualRecurrenceRuleUpdater,
    ManualRecurrenceRuleViewer,
} from "./manual/modifyManualSchedulingBody";
import {
    PartialMonthlyRecurrenceRuleUpdater,
    PartialMonthlyRecurrenceRuleViewer,
    FullMonthlyRecurrenceRuleUpdater,
} from "./monthly/modifyMonthlySchedulingBody";
import { MonthlyCustomDueDatesModal } from "./monthly/monthlyCustomDueDatesModal";
import {
    PartialQuarterlyRecurrenceRuleUpdater,
    PartialQuarterlyRecurrenceRuleViewer,
    FullQuarterlyRecurrenceRuleUpdater,
} from "./quarterly/modifyQuarterlySchedulingBody";
import { QuarterlyCustomDueDatesModal } from "./quarterly/quarterlyCustomDueDatesModal";
import * as theme from "./theme.css";
import {
    FullEveryTwoMonthsRecurrenceRuleUpdater,
    PartialEveryTwoMonthsRecurrenceRuleUpdater,
    PartialEveryTwoMonthsRecurrenceRuleViewer,
} from "./twoMonths/modifyEveryTwoMonthsSchedulingBody";
import { TwoMonthsCustomDueDatesModal } from "./twoMonths/twoMonthsCustomDueDatesModal";
import { DEFAULT_DUE_DATE_OFFSET } from "./types";
import {
    PartialWeeklyRecurrenceRuleUpdater,
    PartialWeeklyRecurrenceRuleViewer,
    FullWeeklyRecurrenceRuleUpdater,
} from "./weekly/modifyWeeklySchedulingBody";
import {
    FullYearlyRecurrenceRuleUpdater,
    PartialYearlyRecurrenceRuleUpdater,
    PartialYearlyRecurrenceRuleViewer,
} from "./yearly/modifyYearlySchedulingBody";

type UpdatableControlSchedulingForm = Omit<
    UpdatableControlScheduling,
    "rule"
> & { rule: UpdatableControlRecurrenceRule };
type UpdatableControlAllocatedTimesForm = Partial<
    Record<StepIndex, StepAllocatedTime>
>;

interface ModifySchedulingFormProps {
    ajaxManager: AjaxManager;
    readOnly: "EDIT" | "READ_ONLY";
    allTags: UserTagOverviewListDto;
    controlIds: Array<Guid>;
    initialControlSchedulings: Array<ControlScheduling | null>;
    initialStepGroupAllocatedTimes: Array<StepGroupAllocatedTimes>;
    initialSchedulingType: SchedulingType;
    calendars: CalendarView[];
    financialYear: FinancialYearView | null;
}

interface StepGroupAllocatedTimes {
    initialAllocatedTime: StepAllocatedTime;
    subsequentAllocatedTimes: Record<SubsequentStepIndex, StepAllocatedTime>;
}

interface ModifySchedulingFormState {
    schedulingType: SchedulingType;
    patchData: {
        scheduling: UpdatableControlSchedulingForm;
        allocatedTimes: UpdatableControlAllocatedTimesForm;
    };
    currentValues: UpdatableControlSchedulingCurrentValues;
    errors: Partial<ErrorMap<ErrorKey>>;
}

interface UpdatableControlSchedulingCurrentValues {
    rule: RecurrenceRuleWithArrays;
    startDate: Moment[];
    coveredPeriod: (ControlCoveredPeriod | null)[];
    dueTime: Time[];
    timezoneName: string[];
    notificationDays: number[];
    notificationTime: Time[];
    reviewerAllocatedTimes: Record<
        SubsequentStepIndex,
        ReviewerAllocatedTime[]
    >;
    remindersBefore: number[][];
    remindersAfter: number[][];
}

function getFieldError(
    errors: Partial<ErrorMap<ErrorKey>>,
    key: ErrorKey
): ErrorValue {
    return errors[key];
}

class ModifySchedulingForm extends React.PureComponent<
    ModifySchedulingFormProps,
    ModifySchedulingFormState
> {
    private oldStartDate: Moment[] | undefined = undefined;

    private defaultRule = memoizeOne(
        (schedulingType: SchedulingType): UpdatableControlRecurrenceRule => {
            switch (schedulingType) {
                case "Manual":
                    return {
                        type: "Date",
                    };
                case "Weekly":
                    return {
                        type: "Weekly",
                        kind: "Partial",
                        data: {},
                    };
                case "Monthly":
                    return {
                        type: "Monthly",
                        kind: "Partial",
                        data: {},
                    };
                case "EveryTwoMonths":
                    return {
                        type: "EveryTwoMonths",
                        kind: "Partial",
                        data: {},
                    };
                case "Quarterly":
                    return {
                        type: "Quarterly",
                        kind: "Partial",
                        data: {},
                    };
                case "HalfYearly":
                    return {
                        type: "HalfYearly",
                        kind: "Partial",
                        data: {},
                    };
                case "Yearly":
                    return {
                        type: "Yearly",
                        kind: "Partial",
                        data: {},
                    };
                default:
                    return assertNever(schedulingType);
            }
        }
    );

    private defaultCurrentRuleWithArrays = memoizeOne(
        (schedulingType: SchedulingType): RecurrenceRuleWithArrays => {
            switch (schedulingType) {
                case "Manual":
                    return {
                        type: "Date",
                        kind: "Full",
                    };
                case "Weekly":
                    return {
                        type: "Weekly",
                        kind: "Full",
                        data: {
                            days: [],
                            periodicity: [],
                        },
                    };
                case "Monthly":
                    return {
                        type: "Monthly",
                        kind: "Full",
                        data: {
                            calendarId: [],
                            defaultDueDateOffset: {
                                offsetPeriod: {
                                    months: [],
                                    days: [],
                                },
                                anchor: [],
                                offsetType: [],
                            },
                            occurrences: {
                                January: [],
                                February: [],
                                March: [],
                                April: [],
                                May: [],
                                June: [],
                                July: [],
                                August: [],
                                September: [],
                                October: [],
                                November: [],
                                December: [],
                            },
                        },
                    };
                case "EveryTwoMonths":
                    return {
                        type: "EveryTwoMonths",
                        kind: "Full",
                        data: {
                            calendarId: [],
                            defaultDueDateOffset: {
                                offsetPeriod: {
                                    months: [],
                                    days: [],
                                },
                                anchor: [],
                                offsetType: [],
                            },
                            occurrences: {
                                JanuaryFebruary: [],
                                MarchApril: [],
                                MayJune: [],
                                JulyAugust: [],
                                SeptemberOctober: [],
                                NovemberDecember: [],
                            },
                        },
                    };
                case "Quarterly":
                    return {
                        type: "Quarterly",
                        kind: "Full",
                        data: {
                            calendarId: [],
                            defaultDueDateOffset: {
                                offsetPeriod: {
                                    months: [],
                                    days: [],
                                },
                                anchor: [],
                                offsetType: [],
                            },
                            financialYearId: [],
                            occurrences: {
                                First: [],
                                Second: [],
                                Third: [],
                                Fourth: [],
                            },
                        },
                    };
                case "HalfYearly":
                    return {
                        type: "HalfYearly",
                        kind: "Full",
                        data: {
                            calendarId: [],
                            defaultDueDateOffset: {
                                offsetPeriod: {
                                    months: [],
                                    days: [],
                                },
                                anchor: [],
                                offsetType: [],
                            },
                            financialYearId: [],
                            occurrences: {
                                First: [],
                                Second: [],
                            },
                        },
                    };
                case "Yearly":
                    return {
                        type: "Yearly",
                        kind: "Full",
                        data: {
                            calendarId: [],
                            defaultDueDateOffset: {
                                offsetPeriod: {
                                    months: [],
                                    days: [],
                                },
                                anchor: [],
                                offsetType: [],
                            },
                            financialYearId: [],
                            periodicity: [],
                        },
                    };
                default:
                    return assertNever(schedulingType);
            }
        }
    );

    constructor(props: ModifySchedulingFormProps) {
        super(props);

        const currentValues = this.generateInitialValues(
            props.initialControlSchedulings,
            props.initialStepGroupAllocatedTimes,
            props.initialSchedulingType
        );

        this.state = {
            schedulingType: props.initialSchedulingType,
            patchData: {
                scheduling: {
                    rule: this.defaultRule(props.initialSchedulingType),
                },
                allocatedTimes: {},
            },
            errors: {},
            currentValues,
        };
    }

    private generateInitialWeeklyRule = (
        rule: WeeklyRecurrenceRule,
        currentRule: WithArrays<WeeklyRecurrenceRule>
    ) => {
        currentRule.periodicity.push(rule.periodicity);
        currentRule.days.push(rule.days);
    };

    private generateInitialMonthlyRule = (
        rule: MonthlyRecurrenceRule,
        currentRule: MonthlyRecurrenceRuleWithArrays
    ) => {
        currentRule.calendarId.push(rule.calendarId);
        currentRule.defaultDueDateOffset.anchor.push(
            rule.defaultDueDateOffset.anchor
        );
        currentRule.defaultDueDateOffset.offsetType.push(
            rule.defaultDueDateOffset.offsetType
        );
        currentRule.defaultDueDateOffset.offsetPeriod.days.push(
            rule.defaultDueDateOffset.offsetPeriod.days
        );
        currentRule.defaultDueDateOffset.offsetPeriod.months.push(
            rule.defaultDueDateOffset.offsetPeriod.months
        );
        currentRule.occurrences = addMonthlyOccurrences(
            currentRule.occurrences,
            rule.occurrences
        );
    };

    private generateInitialEveryTwoMonthsRule = (
        rule: EveryTwoMonthsRecurrenceRule,
        currentRule: EveryTwoMonthsRecurrenceRuleWithArrays
    ) => {
        currentRule.calendarId.push(rule.calendarId);
        currentRule.defaultDueDateOffset.anchor.push(
            rule.defaultDueDateOffset.anchor
        );
        currentRule.defaultDueDateOffset.offsetType.push(
            rule.defaultDueDateOffset.offsetType
        );
        currentRule.defaultDueDateOffset.offsetPeriod.days.push(
            rule.defaultDueDateOffset.offsetPeriod.days
        );
        currentRule.defaultDueDateOffset.offsetPeriod.months.push(
            rule.defaultDueDateOffset.offsetPeriod.months
        );
        currentRule.occurrences = addTwoMonthsOccurrences(
            currentRule.occurrences,
            rule.occurrences
        );
    };

    private generateInitialQuarterlyRule = (
        rule: QuarterlyRecurrenceRule,
        currentRule: QuarterlyRecurrenceRuleWithArrays
    ) => {
        currentRule.calendarId.push(rule.calendarId);
        currentRule.defaultDueDateOffset.anchor.push(
            rule.defaultDueDateOffset.anchor
        );
        currentRule.defaultDueDateOffset.offsetType.push(
            rule.defaultDueDateOffset.offsetType
        );
        currentRule.defaultDueDateOffset.offsetPeriod.days.push(
            rule.defaultDueDateOffset.offsetPeriod.days
        );
        currentRule.defaultDueDateOffset.offsetPeriod.months.push(
            rule.defaultDueDateOffset.offsetPeriod.months
        );
        currentRule.financialYearId.push(rule.financialYearId);
        currentRule.occurrences = addQuarterlyOccurrences(
            currentRule.occurrences,
            rule.occurrences
        );
    };

    private generateInitialHalfYearlyRule = (
        rule: HalfYearlyRecurrenceRule,
        currentRule: HalfYearlyRecurrenceRuleWithArrays
    ) => {
        currentRule.calendarId.push(rule.calendarId);
        currentRule.defaultDueDateOffset.anchor.push(
            rule.defaultDueDateOffset.anchor
        );
        currentRule.defaultDueDateOffset.offsetType.push(
            rule.defaultDueDateOffset.offsetType
        );
        currentRule.defaultDueDateOffset.offsetPeriod.days.push(
            rule.defaultDueDateOffset.offsetPeriod.days
        );
        currentRule.defaultDueDateOffset.offsetPeriod.months.push(
            rule.defaultDueDateOffset.offsetPeriod.months
        );
        currentRule.financialYearId.push(rule.financialYearId);
        currentRule.occurrences = addHalfYearlyOccurrences(
            currentRule.occurrences,
            rule.occurrences
        );
    };

    private generateInitialYearlyRule = (
        rule: YearlyRecurrenceRule,
        currentRule: YearlyRecurrenceRuleWithArrays
    ) => {
        currentRule.calendarId.push(rule.calendarId);
        currentRule.defaultDueDateOffset.anchor.push(
            rule.defaultDueDateOffset.anchor
        );
        currentRule.defaultDueDateOffset.offsetType.push(
            rule.defaultDueDateOffset.offsetType
        );
        currentRule.defaultDueDateOffset.offsetPeriod.days.push(
            rule.defaultDueDateOffset.offsetPeriod.days
        );
        currentRule.defaultDueDateOffset.offsetPeriod.months.push(
            rule.defaultDueDateOffset.offsetPeriod.months
        );
        currentRule.financialYearId.push(rule.financialYearId);
        currentRule.periodicity.push(rule.periodicity);
    };

    private generateInitialValues = (
        controlSchedulings: Array<ControlScheduling | null>,
        stepGroupAllocatedTimes: Array<StepGroupAllocatedTimes>,
        schedulingType: SchedulingType
    ): UpdatableControlSchedulingCurrentValues => {
        const currentValues: UpdatableControlSchedulingCurrentValues = {
            rule: this.defaultCurrentRuleWithArrays(schedulingType),
            startDate: [],
            coveredPeriod: [],
            dueTime: [],
            timezoneName: [],
            notificationDays: [],
            notificationTime: [],
            reviewerAllocatedTimes: {
                SubsequentStep0: [],
                SubsequentStep1: [],
            },
            remindersBefore: [],
            remindersAfter: [],
        };

        for (const scheduling of controlSchedulings) {
            if (scheduling) {
                const time = timeFromString(scheduling.sendTime);
                const dueTime = timeFromString(scheduling.dueTime);
                currentValues.notificationTime.push(time);
                currentValues.coveredPeriod.push(
                    scheduling.coveredPeriod ?? null
                );
                currentValues.dueTime.push(dueTime);
                currentValues.startDate.push(moment(scheduling.startDate));
                currentValues.timezoneName.push(scheduling.timezoneName);
                currentValues.remindersBefore.push(scheduling.remindersBefore);
                currentValues.remindersAfter.push(scheduling.remindersAfter);

                switch (scheduling.rule.type) {
                    case "Date":
                        break;
                    case "Weekly":
                        if (currentValues.rule.type === scheduling.rule.type) {
                            this.generateInitialWeeklyRule(
                                scheduling.rule,
                                currentValues.rule.data
                            );
                        }
                        break;
                    case "Monthly":
                        if (currentValues.rule.type === scheduling.rule.type) {
                            this.generateInitialMonthlyRule(
                                scheduling.rule,
                                currentValues.rule.data
                            );
                        }
                        break;
                    case "EveryTwoMonths":
                        if (currentValues.rule.type === scheduling.rule.type) {
                            this.generateInitialEveryTwoMonthsRule(
                                scheduling.rule,
                                currentValues.rule.data
                            );
                        }
                        break;
                    case "Quarterly":
                        if (currentValues.rule.type === scheduling.rule.type) {
                            this.generateInitialQuarterlyRule(
                                scheduling.rule,
                                currentValues.rule.data
                            );
                        }
                        break;
                    case "HalfYearly":
                        if (currentValues.rule.type === scheduling.rule.type) {
                            this.generateInitialHalfYearlyRule(
                                scheduling.rule,
                                currentValues.rule.data
                            );
                        }
                        break;
                    case "Yearly":
                        if (currentValues.rule.type === scheduling.rule.type) {
                            this.generateInitialYearlyRule(
                                scheduling.rule,
                                currentValues.rule.data
                            );
                        }
                        break;
                    default:
                        return assertNever(scheduling.rule);
                }
            }
        }

        for (const allocatedTimes of stepGroupAllocatedTimes) {
            if (allocatedTimes.initialAllocatedTime.microseconds !== 0) {
                throw new Error(
                    "Responsible allocated time should have microseconds == 0"
                );
            }
            currentValues.notificationDays.push(
                allocatedTimes.initialAllocatedTime.days
            );
            const iterable: Array<
                [StepAllocatedTime, Array<ReviewerAllocatedTime>, string]
            > = [
                [
                    allocatedTimes.subsequentAllocatedTimes["SubsequentStep0"],
                    currentValues.reviewerAllocatedTimes["SubsequentStep0"],
                    "First reviewer",
                ],
                [
                    allocatedTimes.subsequentAllocatedTimes["SubsequentStep1"],
                    currentValues.reviewerAllocatedTimes["SubsequentStep1"],
                    "Second reviewer",
                ],
            ];
            for (const [at, values, what] of iterable) {
                if (at.days !== 0 && at.microseconds !== 0) {
                    throw new Error(
                        `${what} allocated time has days (${at.days}) and microseconds (${at.microseconds}), it is not supported`
                    );
                }
                if (at.microseconds !== 0) {
                    if (at.microseconds % MICROSECONDS_IN_AN_HOUR !== 0) {
                        throw new Error(
                            `${what} allocated time microseconds has more granularity than an hour (${at.microseconds}), it is not supported`
                        );
                    }
                    const offset = Math.floor(
                        at.microseconds / MICROSECONDS_IN_AN_HOUR
                    );
                    values.push({ offset, unit: ReviewerOffsetUnit.HOUR });
                } else {
                    values.push({
                        offset: at.days,
                        unit: ReviewerOffsetUnit.DAY,
                    });
                }
            }
        }

        return currentValues;
    };

    private buildPeriodWidget = memoizeOne(
        (coveredPeriod: (ControlCoveredPeriod | null)[]) =>
            (readOnly: "EDIT" | "READ_ONLY") => {
                let helpText: string | undefined;
                if (readOnly !== "READ_ONLY") {
                    if (this.state.schedulingType === "Manual") {
                        helpText = t(
                            "Control_ModifySchedule_Period_Info_Manual"
                        );
                    } else {
                        helpText = t(
                            "Control_ModifySchedule_Period_Info_Other"
                        );
                    }
                }
                return (
                    <MultiPeriodInput
                        readOnly={readOnly}
                        helpText={helpText}
                        initialValues={
                            coveredPeriod.filter(isNotUndefined) || []
                        }
                        onChange={this.onChangePeriod}
                    />
                );
            }
    );

    private onChangePeriod = (changes: ControlCoveredPeriod | null) => {
        this.setState((prevState) => {
            const { patchData, currentValues } = prevState;
            return {
                patchData: set(patchData, {
                    scheduling: set(patchData.scheduling, {
                        coveredPeriod: changes,
                    }),
                }),
                currentValues: set(currentValues, {
                    coveredPeriod: [changes],
                }),
            };
        });
    };

    private buildDueTimeAndTimezoneWidget = (
        dueTime: Time[],
        timezoneName: string[],
        readOnly: "EDIT" | "READ_ONLY"
    ) => {
        switch (readOnly) {
            case "READ_ONLY": {
                return (
                    <MultiDueTimeAndTimezoneInput
                        type="READ_ONLY"
                        dueTimes={dueTime}
                        timezones={timezoneName}
                    />
                );
            }
            case "EDIT": {
                const handleChange = (change: DueTimeOrTimezoneChange) => {
                    switch (change.kind) {
                        case "DUE_TIME": {
                            const dueTime: string = timeToString(change.value);
                            this.setState((prevState) => {
                                const { patchData, currentValues } = prevState;
                                return {
                                    patchData: set(patchData, {
                                        scheduling: set(patchData.scheduling, {
                                            dueTime,
                                        }),
                                    }),
                                    currentValues: set(currentValues, {
                                        dueTime: [change.value],
                                    }),
                                };
                            });
                            break;
                        }
                        case "TIMEZONE": {
                            const timezoneName: string = change.value;
                            this.setState((prevState) => {
                                const { patchData, currentValues } = prevState;
                                return {
                                    patchData: set(patchData, {
                                        scheduling: set(patchData.scheduling, {
                                            timezoneName,
                                        }),
                                    }),
                                    currentValues: set(currentValues, {
                                        timezoneName: [timezoneName],
                                    }),
                                };
                            });
                            break;
                        }
                        default: {
                            assertNever(change);
                        }
                    }
                };
                return (
                    <MultiDueTimeAndTimezoneInput
                        type="EDIT"
                        dueTimes={dueTime}
                        timezones={timezoneName}
                        onChange={handleChange}
                    />
                );
            }
            default: {
                return assertNever(readOnly);
            }
        }
    };

    private buildStartingDateSelector = memoizeOne(
        (startDate: Moment[]) => () => {
            const { readOnly } = this.props;
            const { schedulingType } = this.state;

            let labelType: StartDateLabelType = "StartDate";
            if (schedulingType === "Manual") {
                labelType = "OnceDate";
            }

            switch (readOnly) {
                case "EDIT":
                    return (
                        <StartingFromDatePicker
                            className={theme.startingFromPicker}
                            readOnly={"EDIT"}
                            values={startDate}
                            onChange={this.onChangeStartDate}
                            labelType={labelType}
                        />
                    );
                case "READ_ONLY":
                    return (
                        <StartingFromDatePicker
                            className={theme.startingFromPicker}
                            readOnly={"READ_ONLY"}
                            values={startDate}
                            labelType={labelType}
                        />
                    );
                default:
                    assertNever(readOnly);
            }
        }
    );

    private onChangeStartDate = (startDate: moment.Moment) => {
        this.setState((prevState) => {
            const { patchData, currentValues } = prevState;
            return {
                patchData: set(patchData, {
                    scheduling: set(patchData.scheduling, {
                        startDate: startDate.format("YYYY-MM-DD"),
                    }),
                }),
                currentValues: set(currentValues, {
                    startDate: [startDate],
                }),
            };
        });
    };

    private buildResponsibleAllocatedTimeInputs = memoizeOne(
        (notificationDays: number[], notificationTime: Time[]) =>
            (readOnly: "EDIT" | "READ_ONLY") => {
                return (
                    <MultiResponsibleAllocatedTimeInput
                        type={readOnly}
                        days={notificationDays}
                        times={notificationTime}
                        onChangeTime={this.onChangeNotificationTime}
                        onChangeDays={this.onChangeNotificationDays}
                    />
                );
            }
    );

    private onChangeNotificationDays = (newDays: number) => {
        this.setState((prevState) => {
            const { patchData, currentValues } = prevState;
            return {
                patchData: set(patchData, {
                    allocatedTimes: set(patchData.allocatedTimes, {
                        Step0: { days: newDays, microseconds: 0 },
                    }),
                }),
                currentValues: set(currentValues, {
                    notificationDays: [newDays],
                }),
            };
        });
    };

    private onChangeNotificationTime = (newTime: Time) => {
        this.setState((prevState) => {
            const { patchData, currentValues } = prevState;
            return {
                patchData: set(patchData, {
                    scheduling: set(patchData.scheduling, {
                        sendTime: timeToString(newTime),
                    }),
                }),
                currentValues: set(currentValues, {
                    notificationTime: [newTime],
                }),
            };
        });
    };

    private buildReviewerAllocatedTimeInputs = memoizeOne(
        (
            reviewerAllocatedTimes: Record<
                SubsequentStepIndex,
                ReviewerAllocatedTime[]
            >
        ) =>
            (
                readOnly: "EDIT" | "READ_ONLY",
                subsequentStepIndex: SubsequentStepIndex
            ) => {
                const stepReviewerAllocatedTimes =
                    reviewerAllocatedTimes[subsequentStepIndex];
                let transTarget: string;
                let taskLabel: string;
                let onChange: (newAllocatedTime: ReviewerAllocatedTime) => void;
                switch (subsequentStepIndex) {
                    case "SubsequentStep0": {
                        transTarget = t(
                            "CreateControl_Notification_FirstReviewerHas"
                        );
                        taskLabel = t(
                            `CreateControl_Notification_Task_FirstReview`
                        );
                        onChange = this.onChangeFirstReviewerAllocatedTime;
                        break;
                    }
                    case "SubsequentStep1": {
                        transTarget = t(
                            "CreateControl_Notification_SecondReviewerHas"
                        );
                        taskLabel = t(
                            `CreateControl_Notification_Task_SecondReview`
                        );
                        onChange = this.onChangeSecondReviewerAllocatedTime;
                        break;
                    }
                    default: {
                        return assertNever(subsequentStepIndex);
                    }
                }

                const toPerform = t(
                    "CreateControl_Notification_AssigneeToPerform",
                    { taskLabel }
                );

                return (
                    <MultiReviewerAllocatedTimeInput
                        type={readOnly}
                        label={transTarget}
                        reviewerAllocatedTimes={stepReviewerAllocatedTimes}
                        toPerformLabel={toPerform}
                        onChange={onChange}
                    />
                );
            }
    );

    private onChangeFirstReviewerAllocatedTime = (
        newAllocatedTime: ReviewerAllocatedTime
    ): void => {
        this.onChangeReviewerAllocatedTime("SubsequentStep0", newAllocatedTime);
    };

    private onChangeSecondReviewerAllocatedTime = (
        newAllocatedTime: ReviewerAllocatedTime
    ): void => {
        this.onChangeReviewerAllocatedTime("SubsequentStep1", newAllocatedTime);
    };

    private onChangeReviewerAllocatedTime = (
        subsequentStepIndex: SubsequentStepIndex,
        newAllocatedTime: ReviewerAllocatedTime
    ): void => {
        let allocatedTime: StepAllocatedTime;
        switch (newAllocatedTime.unit) {
            case "Day": {
                allocatedTime = {
                    days: newAllocatedTime.offset,
                    microseconds: 0,
                };
                break;
            }
            case "Hour": {
                allocatedTime = {
                    days: 0,
                    microseconds:
                        newAllocatedTime.offset * MICROSECONDS_IN_AN_HOUR,
                };
                break;
            }
            default: {
                return assertNever(newAllocatedTime.unit);
            }
        }
        this.setState((prevState) => {
            const { patchData, currentValues } = prevState;
            let stepIndex: StepIndex;
            switch (subsequentStepIndex) {
                case "SubsequentStep0": {
                    stepIndex = "Step1";
                    break;
                }
                case "SubsequentStep1": {
                    stepIndex = "Step2";
                    break;
                }
                default: {
                    return assertNever(subsequentStepIndex);
                }
            }
            return {
                patchData: set(patchData, {
                    allocatedTimes: set(patchData.allocatedTimes, {
                        [stepIndex]: allocatedTime,
                    }),
                }),
                currentValues: set(currentValues, {
                    reviewerAllocatedTimes: {
                        ...currentValues.reviewerAllocatedTimes,
                        [subsequentStepIndex]: [newAllocatedTime],
                    },
                }),
            };
        });
    };

    private buildRemindersWidget = memoizeOne(
        (remindersBefore: number[][], remindersAfter: number[][]) =>
            (readOnly: "EDIT" | "READ_ONLY") => {
                return (
                    <RemindersBody
                        readOnly={readOnly}
                        remindersBefore={remindersBefore}
                        remindersAfter={remindersAfter}
                        onChangeRemindersBefore={this.onChangeRemindersBefore}
                        onChangeRemindersAfter={this.onChangeRemindersAfter}
                    />
                );
            }
    );

    private onChangeRemindersBefore = (changes: number[]) => {
        this.setState((prevState) => {
            const { patchData, currentValues } = prevState;
            return {
                patchData: set(patchData, {
                    scheduling: set(patchData.scheduling, {
                        remindersBefore: changes,
                    }),
                }),
                currentValues: set(currentValues, {
                    remindersBefore: [changes],
                }),
            };
        });
    };

    private onChangeRemindersAfter = (changes: number[]) => {
        this.setState((prevState) => {
            const { patchData, currentValues } = prevState;
            return {
                patchData: set(patchData, {
                    scheduling: set(patchData.scheduling, {
                        remindersAfter: changes,
                    }),
                }),
                currentValues: set(currentValues, {
                    remindersAfter: [changes],
                }),
            };
        });
    };

    private validateManual(): boolean {
        return true;
    }

    private validateWeekly(): boolean {
        return this.validateWeekdays();
    }

    private validateMonthly(): boolean {
        return true;
    }

    private validateEveryTwoMonths(): boolean {
        return true;
    }

    private validateQuarterly(): boolean {
        return true;
    }

    private validateHalfYearly(): boolean {
        return true;
    }

    private validateYearly(): boolean {
        return true;
    }

    private validateFields(): boolean {
        const { schedulingType } = this.state;
        switch (schedulingType) {
            case "Manual":
                return this.validateManual();
            case "Weekly":
                return this.validateWeekly();
            case "Monthly":
                return this.validateMonthly();
            case "EveryTwoMonths":
                return this.validateEveryTwoMonths();
            case "Quarterly":
                return this.validateQuarterly();
            case "HalfYearly":
                return this.validateHalfYearly();
            case "Yearly":
                return this.validateYearly();
            default:
                assertNever(schedulingType);
        }
    }

    validate():
        | {
              scheduling: UpdatableControlSchedulingForm;
              allocatedTimes: UpdatableControlAllocatedTimesForm;
          }
        | undefined {
        if (!this.validateFields()) {
            return undefined;
        }
        const { patchData } = this.state;
        return patchData;
    }

    private buildDefaultDueDateOffsetInput = memoizeOne(
        (
            rule: RecurrenceRuleWithArrays,
            dueTime: Time[],
            timezoneName: string[]
        ) =>
            (
                readOnly: "EDIT" | "READ_ONLY",
                period: PeriodType
            ): React.ReactNode => {
                const { calendars } = this.props;

                if (rule.type === "Date" || rule.type === "Weekly") {
                    throw new Error(
                        "Date and Weekly rules have no defaultDueDateOffset"
                    );
                }

                const { calendarId, defaultDueDateOffset } = rule.data;
                const values: MultiDefaultDueDateOffsetValues = {
                    calendarIds: calendarId.map((id) => id ?? null),
                    months: defaultDueDateOffset.offsetPeriod.months,
                    days: defaultDueDateOffset.offsetPeriod.days,
                    offsetTypes: defaultDueDateOffset.offsetType,
                    anchors: defaultDueDateOffset.anchor,
                };

                switch (readOnly) {
                    case "READ_ONLY": {
                        return (
                            <MultiDefaultDueDateOffsetInput
                                type="READ_ONLY"
                                values={values}
                                calendars={calendars}
                                period={period}
                                textAfter={t("CreateControl_Recurrence_At")}
                                nodeAfter={this.buildDueTimeAndTimezoneWidget(
                                    dueTime,
                                    timezoneName,
                                    readOnly
                                )}
                            />
                        );
                    }
                    case "EDIT": {
                        return (
                            <MultiDefaultDueDateOffsetInput
                                type="EDIT"
                                values={values}
                                calendars={calendars}
                                period={period}
                                textAfter={t("CreateControl_Recurrence_At")}
                                nodeAfter={this.buildDueTimeAndTimezoneWidget(
                                    dueTime,
                                    timezoneName,
                                    readOnly
                                )}
                                onChangeMonths={
                                    this.onChangeDefaultDueDateOffsetMonths
                                }
                                onChangeDays={
                                    this.onChangeDefaultDueDateOffsetDays
                                }
                                onChangeCalendarId={this.onChangeCalendarId}
                                onChangeOffsetType={
                                    this.onChangeDefaultDueDateOffsetType
                                }
                                onChangeAnchor={
                                    this.onChangeDefaultDueDateOffsetAnchor
                                }
                            />
                        );
                    }
                    default: {
                        return assertNever(readOnly);
                    }
                }
            }
    );

    private buildManualDueDateAndTimeInputs = memoizeOne(
        (startDate: Moment[], dueTime: Time[], timezoneName: string[]) =>
            (readOnly: "EDIT" | "READ_ONLY"): React.ReactNode => {
                let datePicker: React.ReactNode;
                switch (readOnly) {
                    case "READ_ONLY": {
                        datePicker = (
                            <RawReadOnlyDatePicker
                                name="startingFromDate"
                                values={startDate}
                            />
                        );
                        break;
                    }
                    case "EDIT": {
                        datePicker = (
                            <RawEditableDatePicker
                                name="startingFromDate"
                                onChange={(val) => {
                                    if (val) {
                                        this.onChangeStartDate(val);
                                    }
                                }}
                                values={startDate}
                                allowClear={false}
                            />
                        );
                        break;
                    }
                    default: {
                        return assertNever(readOnly);
                    }
                }

                return (
                    <BaseFormDisplay label={translateLabel("OnceDate")}>
                        <div className={theme.manualDueDateAndTime}>
                            {datePicker}
                            {t("CreateControl_Recurrence_At")}
                            {this.buildDueTimeAndTimezoneWidget(
                                dueTime,
                                timezoneName,
                                readOnly
                            )}
                        </div>
                    </BaseFormDisplay>
                );
            }
    );

    private onChangeDefaultDueDateOffsetMonths = (months: number) => {
        this.setState((prevState) => {
            const { patchData, currentValues } = prevState;
            if (
                patchData.scheduling.rule.type === "Weekly" ||
                currentValues.rule.type === "Weekly" ||
                patchData.scheduling.rule.type === "Date" ||
                currentValues.rule.type === "Date"
            ) {
                throw new Error(
                    "Date and Weekly rules have no defaultDueDateOffset"
                );
            }
            // SAFETY:
            // Remaining scheduling rules (Monthly, EveryTwoMonths, Quarterly, HalfYearly, Yearly) have `defaultDueDateOffset` field.
            // In case of Full update `defaultDueDateOffset` and `offsetPeriod` are guaranteed not to be undefined, so a new full structure is created with `months` field changed.
            // In case of Partial update if `defaultDueDateOffset` and/or `offsetPeriod` are undefined, then a partial structure with `months` field is created.
            // Otherwise the partial just gets a new field value.
            return {
                patchData: set(patchData, {
                    scheduling: set(patchData.scheduling, {
                        rule: set(patchData.scheduling.rule, {
                            data: set(patchData.scheduling.rule.data, {
                                defaultDueDateOffset: set(
                                    patchData.scheduling.rule.data
                                        .defaultDueDateOffset || {},
                                    {
                                        offsetPeriod: set(
                                            patchData.scheduling.rule.data
                                                .defaultDueDateOffset
                                                ?.offsetPeriod || {},
                                            {
                                                months,
                                            }
                                        ),
                                    }
                                ),
                            }),
                        }),
                    }),
                }),
                currentValues: set(currentValues, {
                    rule: set(currentValues.rule, {
                        data: set(currentValues.rule.data, {
                            defaultDueDateOffset: set(
                                currentValues.rule.data.defaultDueDateOffset,
                                {
                                    offsetPeriod: set(
                                        currentValues.rule.data
                                            .defaultDueDateOffset.offsetPeriod,
                                        {
                                            months: [months],
                                        }
                                    ),
                                }
                            ),
                        }),
                    }),
                }),
            };
        });
    };

    private onChangeDefaultDueDateOffsetDays = (days: number) => {
        this.setState((prevState) => {
            const { patchData, currentValues } = prevState;
            if (
                patchData.scheduling.rule.type === "Weekly" ||
                currentValues.rule.type === "Weekly" ||
                patchData.scheduling.rule.type === "Date" ||
                currentValues.rule.type === "Date"
            ) {
                throw new Error(
                    "Date and Weekly rules have no defaultDueDateOffset"
                );
            }
            // SAFETY:
            // Remaining scheduling rules (Monthly, EveryTwoMonths, Quarterly, HalfYearly, Yearly) have `defaultDueDateOffset` field.
            // In case of Full update `defaultDueDateOffset` and `offsetPeriod` are guaranteed not to be undefined, so a new full structure is created with `days` field changed.
            // In case of Partial update if `defaultDueDateOffset` and/or `offsetPeriod` are undefined, then a partial structure with `days` field is created.
            // Otherwise the partial just gets a new field value.
            return {
                patchData: set(patchData, {
                    scheduling: set(patchData.scheduling, {
                        rule: set(patchData.scheduling.rule, {
                            data: set(patchData.scheduling.rule.data, {
                                defaultDueDateOffset: set(
                                    patchData.scheduling.rule.data
                                        .defaultDueDateOffset || {},
                                    {
                                        offsetPeriod: set(
                                            patchData.scheduling.rule.data
                                                .defaultDueDateOffset
                                                ?.offsetPeriod || {},
                                            {
                                                days,
                                            }
                                        ),
                                    }
                                ),
                            }),
                        }),
                    }),
                }),
                currentValues: set(currentValues, {
                    rule: set(currentValues.rule, {
                        data: set(currentValues.rule.data, {
                            defaultDueDateOffset: set(
                                currentValues.rule.data.defaultDueDateOffset,
                                {
                                    offsetPeriod: set(
                                        currentValues.rule.data
                                            .defaultDueDateOffset.offsetPeriod,
                                        {
                                            days: [days],
                                        }
                                    ),
                                }
                            ),
                        }),
                    }),
                }),
            };
        });
    };

    private onChangeDefaultDueDateOffsetType = (offsetType: OffsetType) => {
        this.setState((prevState) => {
            const { patchData, currentValues } = prevState;
            if (
                patchData.scheduling.rule.type === "Weekly" ||
                currentValues.rule.type === "Weekly" ||
                patchData.scheduling.rule.type === "Date" ||
                currentValues.rule.type === "Date"
            ) {
                throw new Error(
                    "Date and Weekly rules have no defaultDueDateOffset"
                );
            }
            // SAFETY:
            // Remaining scheduling rules (Monthly, EveryTwoMonths, Quarterly, HalfYearly, Yearly) have `defaultDueDateOffset` field.
            // In case of Full update `defaultDueDateOffset` are guaranteed not to be undefined, so a new full structure is created with `offsetType` field changed.
            // In case of Partial update if `defaultDueDateOffset` is undefined, then a partial with `offsetType` field is created.
            // Otherwise the partial just gets a new field value.
            return {
                patchData: set(patchData, {
                    scheduling: set(patchData.scheduling, {
                        rule: set(patchData.scheduling.rule, {
                            data: set(patchData.scheduling.rule.data, {
                                defaultDueDateOffset: set(
                                    patchData.scheduling.rule.data
                                        .defaultDueDateOffset || {},
                                    {
                                        offsetType,
                                    }
                                ),
                            }),
                        }),
                    }),
                }),
                currentValues: set(currentValues, {
                    rule: set(currentValues.rule, {
                        data: set(currentValues.rule.data, {
                            defaultDueDateOffset: set(
                                currentValues.rule.data.defaultDueDateOffset,
                                {
                                    offsetType: [offsetType],
                                }
                            ),
                        }),
                    }),
                }),
            };
        });
    };

    private onChangeDefaultDueDateOffsetAnchor = (anchor: OffsetAnchor) => {
        this.setState((prevState) => {
            const { patchData, currentValues } = prevState;
            if (
                patchData.scheduling.rule.type === "Weekly" ||
                currentValues.rule.type === "Weekly" ||
                patchData.scheduling.rule.type === "Date" ||
                currentValues.rule.type === "Date"
            ) {
                throw new Error(
                    "Date and Weekly rules have no defaultDueDateOffset"
                );
            }
            // SAFETY:
            // Remaining scheduling rules (Monthly, EveryTwoMonths, Quarterly, HalfYearly, Yearly) have `defaultDueDateOffset` field.
            // In case of Full update `defaultDueDateOffset` are guaranteed not to be undefined, so a new full structure is created with `offsetType` field changed.
            // In case of Partial update if `defaultDueDateOffset` is undefined, then a partial with `offsetType` field is created.
            // Otherwise the partial just gets a new field value.
            return {
                patchData: set(patchData, {
                    scheduling: set(patchData.scheduling, {
                        rule: set(patchData.scheduling.rule, {
                            data: set(patchData.scheduling.rule.data, {
                                defaultDueDateOffset: set(
                                    patchData.scheduling.rule.data
                                        .defaultDueDateOffset || {},
                                    {
                                        anchor,
                                    }
                                ),
                            }),
                        }),
                    }),
                }),
                currentValues: set(currentValues, {
                    rule: set(currentValues.rule, {
                        data: set(currentValues.rule.data, {
                            defaultDueDateOffset: set(
                                currentValues.rule.data.defaultDueDateOffset,
                                {
                                    anchor: [anchor],
                                }
                            ),
                        }),
                    }),
                }),
            };
        });
    };

    private onChangeCalendarId = (calendarId: number | null) => {
        this.setState((prevState) => {
            const { patchData, currentValues } = prevState;
            if (
                patchData.scheduling.rule.type === "Weekly" ||
                currentValues.rule.type === "Weekly" ||
                patchData.scheduling.rule.type === "Date" ||
                currentValues.rule.type === "Date"
            ) {
                throw new Error(
                    "Date and Weekly rules have no defaultDueDateOffset"
                );
            }
            return {
                patchData: set(patchData, {
                    scheduling: set(patchData.scheduling, {
                        rule: set(patchData.scheduling.rule, {
                            data: set(patchData.scheduling.rule.data, {
                                calendarId,
                            }),
                        }),
                    }),
                }),
                currentValues: set(currentValues, {
                    rule: set(currentValues.rule, {
                        data: set(currentValues.rule.data, {
                            calendarId: [calendarId],
                        }),
                    }),
                }),
            };
        });
    };

    private buildNewSchedulingTypeState = (
        rule: UpdatableControlRecurrenceRule,
        ruleWithArrays: RecurrenceRuleWithArrays,
        coveredPeriod: CoveredPeriodKey,
        startDate: string | undefined,
        currentStartDate: Moment[],
        newSchedulingType: SchedulingType
    ): ModifySchedulingFormState => {
        const { currentValues, patchData } = this.state;
        return {
            schedulingType: newSchedulingType,
            patchData: set(patchData, {
                scheduling: set(patchData.scheduling, {
                    rule,
                    coveredPeriod: COVERED_PERIOD_VALUES[coveredPeriod],
                    startDate,
                }),
            }),
            errors: {},
            currentValues: set(currentValues, {
                rule: ruleWithArrays,
                coveredPeriod: [COVERED_PERIOD_VALUES[coveredPeriod]],
                startDate: currentStartDate,
            }),
        };
    };

    private onChangeSchedulingType = (
        newSchedulingType: SchedulingType
    ): void => {
        let startDate: string | undefined;
        let currentStartDate = this.state.currentValues.startDate;
        if (
            this.state.schedulingType !== "Manual" &&
            newSchedulingType === "Manual"
        ) {
            this.oldStartDate = this.state.currentValues.startDate;
            const startDateMoment = moment().add(1, "day");
            currentStartDate = [startDateMoment];
            startDate = startDateMoment.format("YYYY-MM-DD");
        } else if (
            this.state.schedulingType === "Manual" &&
            newSchedulingType !== "Manual"
        ) {
            currentStartDate =
                this.oldStartDate || this.state.currentValues.startDate;
        }
        const controlCount = this.props.controlIds.length;
        try {
            switch (newSchedulingType) {
                case "Manual":
                    this.setState(
                        this.buildNewSchedulingTypeState(
                            {
                                type: "Date",
                            },
                            {
                                type: "Date",
                                kind: "Full",
                            },
                            "None",
                            startDate,
                            currentStartDate,
                            newSchedulingType
                        )
                    );
                    break;
                case "Weekly":
                    this.setState(
                        this.buildNewSchedulingTypeState(
                            {
                                type: "Weekly",
                                kind: "Full",
                                data: defaultWeeklyRule,
                            },
                            {
                                type: "Weekly",
                                kind: "Full",
                                data: defaultWeeklyRuleWithArrays,
                            },
                            "None",
                            startDate,
                            currentStartDate,
                            newSchedulingType
                        )
                    );
                    break;
                case "Monthly":
                    this.setState(
                        this.buildNewSchedulingTypeState(
                            {
                                type: "Monthly",
                                kind: "Full",
                                data: defaultMonthlyRule,
                            },
                            {
                                type: "Monthly",
                                kind: "Full",
                                data: generateDefaultMonthlyRuleWithArrays(
                                    controlCount
                                ),
                            },
                            "CurrentMonth",
                            startDate,
                            currentStartDate,
                            newSchedulingType
                        )
                    );
                    break;
                case "EveryTwoMonths":
                    this.setState(
                        this.buildNewSchedulingTypeState(
                            {
                                type: "EveryTwoMonths",
                                kind: "Full",
                                data: defaultEveryTwoMonthsRule,
                            },
                            {
                                type: "EveryTwoMonths",
                                kind: "Full",
                                data: generateDefaultEveryTwoMonthsRuleWithArrays(
                                    controlCount
                                ),
                            },
                            "CurrentPeriod",
                            startDate,
                            currentStartDate,
                            newSchedulingType
                        )
                    );
                    break;
                case "Quarterly":
                    this.setState(
                        this.buildNewSchedulingTypeState(
                            {
                                type: "Quarterly",
                                kind: "Full",
                                data: defaultQuarterlyRule,
                            },
                            {
                                type: "Quarterly",
                                kind: "Full",
                                data: generateDefaultQuarterlyRuleWithArrays(
                                    controlCount
                                ),
                            },
                            "CurrentQuarter",
                            startDate,
                            currentStartDate,
                            newSchedulingType
                        )
                    );
                    break;
                case "HalfYearly":
                    this.setState(
                        this.buildNewSchedulingTypeState(
                            {
                                type: "HalfYearly",
                                kind: "Full",
                                data: defaultHalfYearlyRule,
                            },
                            {
                                type: "HalfYearly",
                                kind: "Full",
                                data: generateDefaultHalfYearlyRuleWithArrays(
                                    controlCount
                                ),
                            },
                            "CurrentHalfYear",
                            startDate,
                            currentStartDate,
                            newSchedulingType
                        )
                    );
                    break;
                case "Yearly":
                    this.setState(
                        this.buildNewSchedulingTypeState(
                            {
                                type: "Yearly",
                                kind: "Full",
                                data: defaultYearlyRule,
                            },
                            {
                                type: "Yearly",
                                kind: "Full",
                                data: defaultYearlyRuleWithArrays,
                            },
                            "CurrentYear",
                            startDate,
                            currentStartDate,
                            newSchedulingType
                        )
                    );
                    break;
                default:
                    assertNever(newSchedulingType);
            }
        } catch (e) {
            console.error("Got error while updating patch values: ", e);
            throw e;
        }
    };

    onChangeFinancialYear = (financialYearId: number | null) => {
        this.setState((prevState) => {
            const { patchData, currentValues } = prevState;
            if (
                patchData.scheduling.rule.type === "Weekly" ||
                currentValues.rule.type === "Weekly" ||
                patchData.scheduling.rule.type === "Date" ||
                currentValues.rule.type === "Date" ||
                patchData.scheduling.rule.type === "Monthly" ||
                currentValues.rule.type === "Monthly" ||
                patchData.scheduling.rule.type === "EveryTwoMonths" ||
                currentValues.rule.type === "EveryTwoMonths"
            ) {
                throw new Error(
                    "Date and Weekly rules have no defaultDueDateOffset"
                );
            }
            return {
                patchData: set(patchData, {
                    scheduling: set(patchData.scheduling, {
                        rule: set(patchData.scheduling.rule, {
                            data: set(patchData.scheduling.rule.data, {
                                financialYearId,
                            }),
                        }),
                    }),
                }),
                currentValues: set(currentValues, {
                    rule: set(currentValues.rule, {
                        data: set(currentValues.rule.data, {
                            financialYearId: [financialYearId],
                        }),
                    }),
                }),
            };
        });
    };

    onChangePeriodicity = (periodicity: number) => {
        this.setState((prevState) => {
            const { patchData, currentValues } = prevState;
            if (
                patchData.scheduling.rule.type !== "Weekly" ||
                currentValues.rule.type !== "Weekly"
            ) {
                throw new Error("Only Weekly rules have periodicity");
            }
            return {
                patchData: set(patchData, {
                    scheduling: set(patchData.scheduling, {
                        rule: set(patchData.scheduling.rule, {
                            data: set(patchData.scheduling.rule.data, {
                                periodicity,
                            }),
                        }),
                    }),
                }),
                currentValues: set(currentValues, {
                    rule: set(currentValues.rule, {
                        data: set(currentValues.rule.data, {
                            periodicity: [periodicity],
                        }),
                    }),
                }),
            };
        });
    };

    onChangePartialWeekdays = (day: DayOfWeek, value: boolean) => {
        this.setState((prevState) => {
            const { patchData, currentValues } = prevState;
            if (
                patchData.scheduling.rule.type !== "Weekly" ||
                currentValues.rule.type !== "Weekly" ||
                patchData.scheduling.rule.kind !== "Partial"
            ) {
                throw new Error("Only Weekly rules have weekdays");
            }

            const newPatchDays = applyWeekdayChangesToPatch(
                patchData.scheduling.rule.data.days,
                day,
                value
            );
            const newCurrentValueDays: DayOfWeek[][] =
                currentValues.rule.data.days.map((days) =>
                    applyWeekdayChangesToCurrentValues(days, day, value)
                );

            return {
                patchData: set(patchData, {
                    scheduling: set(patchData.scheduling, {
                        rule: set(patchData.scheduling.rule, {
                            data: set(patchData.scheduling.rule.data, {
                                days: newPatchDays,
                            }),
                        }),
                    }),
                }),
                currentValues: set(currentValues, {
                    rule: set(currentValues.rule, {
                        data: set(currentValues.rule.data, {
                            days: newCurrentValueDays,
                        }),
                    }),
                }),
            };
        });
    };

    onChangeFullWeekdays = (day: DayOfWeek, value: boolean) => {
        this.setState((prevState) => {
            const { patchData, currentValues } = prevState;
            if (
                patchData.scheduling.rule.type !== "Weekly" ||
                currentValues.rule.type !== "Weekly" ||
                patchData.scheduling.rule.kind !== "Full"
            ) {
                throw new Error("Only Weekly rules have weekdays");
            }

            const newPatchDays: DayOfWeek[] =
                applyWeekdayChangesToCurrentValues(
                    patchData.scheduling.rule.data.days,
                    day,
                    value
                );
            const newCurrentValueDays: DayOfWeek[][] =
                currentValues.rule.data.days.map((days) =>
                    applyWeekdayChangesToCurrentValues(days, day, value)
                );

            return {
                patchData: set(patchData, {
                    scheduling: set(patchData.scheduling, {
                        rule: set(patchData.scheduling.rule, {
                            data: set(patchData.scheduling.rule.data, {
                                days: newPatchDays,
                            }),
                        }),
                    }),
                }),
                currentValues: set(currentValues, {
                    rule: set(currentValues.rule, {
                        data: set(currentValues.rule.data, {
                            days: newCurrentValueDays,
                        }),
                    }),
                }),
            };
        });
    };

    /*
     * In case of full update, we use `undefined`, because `undefined` is not serialized into JSON and not send
     * In case of partial update, we use `null`, which instructs backend explicitly to deactivate the period
     * Otherwise we use just Default or Custom periods, which are OK for both partial and full payloads
     */
    transformForFullOrPartial = (
        value: SchedulingOccurrenceChange | undefined
    ): SchedulingOccurrenceChange | null | undefined => {
        const {
            patchData: {
                scheduling: { rule },
            },
        } = this.state;
        if (value === undefined) {
            // If we have a full update we want to remove any disabled interval, instead of sending null
            if (rule.type !== "Date" && rule.kind === "Full") {
                return undefined;
            } else {
                return null;
            }
        } else if (
            value === "ActivateOnlyDeactivated" &&
            rule.type !== "Date" &&
            rule.kind === "Full"
        ) {
            // We don't have 'ActivateOnlyDeactivated' for a Full update
            return "Default";
        } else {
            return value;
        }
    };

    applyChangeToCurrentValues = (
        currentValues: Array<DefaultOrCustomOffset | undefined>,
        change: SchedulingOccurrenceChange | undefined
    ): Array<DefaultOrCustomOffset | undefined> => {
        if (change === "ActivateOnlyDeactivated") {
            return currentValues.map((t) => (!t ? "Default" : t));
        } else {
            return currentValues.map((_x) => change);
        }
    };

    onChangeMonthOccurrence = (
        occurrence: Month,
        value: SchedulingOccurrenceChange | undefined
    ) => {
        this.setState((prevState) => {
            const { patchData, currentValues } = prevState;
            if (
                patchData.scheduling.rule.type !== "Monthly" ||
                currentValues.rule.type !== "Monthly"
            ) {
                throw new Error(
                    "Date and Weekly rules have no defaultDueDateOffset"
                );
            }
            // Needed because we need the count of customized entries in the custom due date dialog. We can't get that if we overwrite all customized occurrences
            const newCurrentValues = this.applyChangeToCurrentValues(
                currentValues.rule.data.occurrences[occurrence],
                value
            );
            return {
                patchData: set(patchData, {
                    scheduling: set(patchData.scheduling, {
                        rule: set(patchData.scheduling.rule, {
                            data: set(patchData.scheduling.rule.data, {
                                occurrences: set(
                                    patchData.scheduling.rule.data.occurrences,
                                    {
                                        [occurrence]:
                                            this.transformForFullOrPartial(
                                                value
                                            ),
                                    }
                                ),
                            }),
                        }),
                    }),
                }),
                currentValues: set(currentValues, {
                    rule: set(currentValues.rule, {
                        data: set(currentValues.rule.data, {
                            occurrences: {
                                ...currentValues.rule.data.occurrences,
                                [occurrence]: newCurrentValues,
                            },
                        }),
                    }),
                }),
            };
        });
    };

    onChangeTwoMonthsOccurrence = (
        occurrence: TwoMonths,
        value: SchedulingOccurrenceChange | undefined
    ) => {
        this.setState((prevState) => {
            const { patchData, currentValues } = prevState;
            if (
                patchData.scheduling.rule.type !== "EveryTwoMonths" ||
                currentValues.rule.type !== "EveryTwoMonths"
            ) {
                throw new Error(
                    "Date and Weekly rules have no defaultDueDateOffset"
                );
            }
            // Needed because we need the count of customized entries in the custom due date dialog. We can't get that if we overwrite all customized occurrences
            const newCurrentValues = this.applyChangeToCurrentValues(
                currentValues.rule.data.occurrences[occurrence],
                value
            );
            return {
                patchData: set(patchData, {
                    scheduling: set(patchData.scheduling, {
                        rule: set(patchData.scheduling.rule, {
                            data: set(patchData.scheduling.rule.data, {
                                occurrences: set(
                                    patchData.scheduling.rule.data.occurrences,
                                    {
                                        [occurrence]:
                                            this.transformForFullOrPartial(
                                                value
                                            ),
                                    }
                                ),
                            }),
                        }),
                    }),
                }),
                currentValues: set(currentValues, {
                    rule: set(currentValues.rule, {
                        data: set(currentValues.rule.data, {
                            occurrences: {
                                ...currentValues.rule.data.occurrences,
                                [occurrence]: newCurrentValues,
                            },
                        }),
                    }),
                }),
            };
        });
    };

    onChangeQuarterlyOccurrence = (
        occurrence: Quarter,
        value: SchedulingOccurrenceChange | undefined
    ) => {
        this.setState((prevState) => {
            const { patchData, currentValues } = prevState;
            if (
                patchData.scheduling.rule.type !== "Quarterly" ||
                currentValues.rule.type !== "Quarterly"
            ) {
                throw new Error(
                    "Date and Weekly rules have no defaultDueDateOffset"
                );
            }
            // Needed because we need the count of customized entries in the custom due date dialog. We can't get that if we overwrite all customized occurrences
            const newCurrentValues = this.applyChangeToCurrentValues(
                currentValues.rule.data.occurrences[occurrence],
                value
            );
            return {
                patchData: set(patchData, {
                    scheduling: set(patchData.scheduling, {
                        rule: set(patchData.scheduling.rule, {
                            data: set(patchData.scheduling.rule.data, {
                                occurrences: set(
                                    patchData.scheduling.rule.data.occurrences,
                                    {
                                        [occurrence]:
                                            this.transformForFullOrPartial(
                                                value
                                            ),
                                    }
                                ),
                            }),
                        }),
                    }),
                }),
                currentValues: set(currentValues, {
                    rule: set(currentValues.rule, {
                        data: set(currentValues.rule.data, {
                            occurrences: {
                                ...currentValues.rule.data.occurrences,
                                [occurrence]: newCurrentValues,
                            },
                        }),
                    }),
                }),
            };
        });
    };

    onChangeHalfYearlyOccurrence = (
        occurrence: HalfYear,
        value: SchedulingOccurrenceChange | undefined
    ) => {
        this.setState((prevState) => {
            const { patchData, currentValues } = prevState;
            if (
                patchData.scheduling.rule.type !== "HalfYearly" ||
                currentValues.rule.type !== "HalfYearly"
            ) {
                throw new Error(
                    "Date and Weekly rules have no defaultDueDateOffset"
                );
            }
            // Needed because we need the count of customized entries in the custom due date dialog. We can't get that if we overwrite all customized occurrences
            const newCurrentValues = this.applyChangeToCurrentValues(
                currentValues.rule.data.occurrences[occurrence],
                value
            );
            return {
                patchData: set(patchData, {
                    scheduling: set(patchData.scheduling, {
                        rule: set(patchData.scheduling.rule, {
                            data: set(patchData.scheduling.rule.data, {
                                occurrences: set(
                                    patchData.scheduling.rule.data.occurrences,
                                    {
                                        [occurrence]:
                                            this.transformForFullOrPartial(
                                                value
                                            ),
                                    }
                                ),
                            }),
                        }),
                    }),
                }),
                currentValues: set(currentValues, {
                    rule: set(currentValues.rule, {
                        data: set(currentValues.rule.data, {
                            occurrences: {
                                ...currentValues.rule.data.occurrences,
                                [occurrence]: newCurrentValues,
                            },
                        }),
                    }),
                }),
            };
        });
    };

    setFieldError = (key: ErrorKey, error: ErrorValue) => {
        this.setState((prevState) => {
            return {
                errors: {
                    ...prevState.errors,
                    [key]: error,
                },
            };
        });
    };

    validateWeekdays = (): boolean => {
        const { currentValues } = this.state;

        if (currentValues.rule.type === "Weekly") {
            const hasEmptyWeekDays = currentValues.rule.data.days.some(
                (days) => days.length === 0
            );
            if (hasEmptyWeekDays) {
                this.setFieldError(
                    "days",
                    t("Recurrence_Validation_Invalid_WeeklyDays")
                );
            }
            return !hasEmptyWeekDays;
        }
        return true;
    };

    buildMonthlyCustomDueDateDialog = memoizeOne(
        (rule: RecurrenceRuleWithArrays) => (): React.ReactNode => {
            const { patchData } = this.state;

            if (
                rule.type === "Monthly" &&
                patchData.scheduling.rule.type === "Monthly"
            ) {
                return (
                    <MonthlyCustomDueDatesModal
                        type={this.props.readOnly}
                        initialCurrentOccurrences={rule.data.occurrences}
                        onChange={this.onChangeMonthOccurrence}
                    />
                );
            } else {
                return <></>;
            }
        }
    );

    buildTwoMonthsCustomDueDateDialog = memoizeOne(
        (rule: RecurrenceRuleWithArrays) => (): React.ReactNode => {
            const { patchData } = this.state;

            if (
                rule.type === "EveryTwoMonths" &&
                patchData.scheduling.rule.type === "EveryTwoMonths"
            ) {
                return (
                    <TwoMonthsCustomDueDatesModal
                        type={this.props.readOnly}
                        initialCurrentOccurrences={rule.data.occurrences}
                        onChange={this.onChangeTwoMonthsOccurrence}
                    />
                );
            } else {
                return <></>;
            }
        }
    );

    buildQuarterlyCustomDueDateDialog = memoizeOne(
        (rule: RecurrenceRuleWithArrays) => (): React.ReactNode => {
            const { patchData } = this.state;

            if (
                rule.type === "Quarterly" &&
                patchData.scheduling.rule.type === "Quarterly"
            ) {
                return (
                    <QuarterlyCustomDueDatesModal
                        type={this.props.readOnly}
                        initialCurrentOccurrences={rule.data.occurrences}
                        onChange={this.onChangeQuarterlyOccurrence}
                    />
                );
            } else {
                return <></>;
            }
        }
    );

    buildHalfYearlyCustomDueDateDialog = memoizeOne(
        (rule: RecurrenceRuleWithArrays) => (): React.ReactNode => {
            const { patchData } = this.state;

            if (
                rule.type === "HalfYearly" &&
                patchData.scheduling.rule.type === "HalfYearly"
            ) {
                return (
                    <HalfYearlyCustomDueDatesModal
                        type={this.props.readOnly}
                        initialCurrentOccurrences={rule.data.occurrences}
                        onChange={this.onChangeHalfYearlyOccurrence}
                    />
                );
            } else {
                return <></>;
            }
        }
    );

    render = () => {
        const { readOnly, financialYear } = this.props;
        const { patchData, currentValues, errors } = this.state;

        return (
            <RuleForm
                errors={errors}
                financialYear={financialYear}
                getFieldError={getFieldError}
                type={readOnly}
                recurenceRule={patchData.scheduling.rule}
                currentRule={currentValues.rule}
                onChangeSchedulingType={this.onChangeSchedulingType}
                onChangeFullWeekdays={this.onChangeFullWeekdays}
                onChangePartialWeekdays={this.onChangePartialWeekdays}
                onChangePeriodicity={this.onChangePeriodicity}
                onChangeFinancialYear={this.onChangeFinancialYear}
                buildPeriodWidget={this.buildPeriodWidget(
                    currentValues.coveredPeriod
                )}
                buildStartingDateSelector={this.buildStartingDateSelector(
                    currentValues.startDate
                )}
                buildResponsibleAllocatedTimeInputs={this.buildResponsibleAllocatedTimeInputs(
                    currentValues.notificationDays,
                    currentValues.notificationTime
                )}
                buildReviewerAllocatedTimeInputs={this.buildReviewerAllocatedTimeInputs(
                    currentValues.reviewerAllocatedTimes
                )}
                buildRemindersWidget={this.buildRemindersWidget(
                    currentValues.remindersBefore,
                    currentValues.remindersAfter
                )}
                buildDefaultDueDateOffsetInput={this.buildDefaultDueDateOffsetInput(
                    currentValues.rule,
                    currentValues.dueTime,
                    currentValues.timezoneName
                )}
                buildManualDueDateAndTimeInputs={this.buildManualDueDateAndTimeInputs(
                    currentValues.startDate,
                    currentValues.dueTime,
                    currentValues.timezoneName
                )}
                onChangeMonthOccurrence={this.onChangeMonthOccurrence}
                buildMonthlyCustomDueDateDialog={this.buildMonthlyCustomDueDateDialog(
                    currentValues.rule
                )}
                onChangeTwoMonthsOccurrence={this.onChangeTwoMonthsOccurrence}
                buildTwoMonthsCustomDueDateDialog={this.buildTwoMonthsCustomDueDateDialog(
                    currentValues.rule
                )}
                onChangeQuarterlyOccurrence={this.onChangeQuarterlyOccurrence}
                buildQuarterlyCustomDueDateDialog={this.buildQuarterlyCustomDueDateDialog(
                    currentValues.rule
                )}
                onChangeHalfYearlyOccurrence={this.onChangeHalfYearlyOccurrence}
                buildHalfYearlyCustomDueDateDialog={this.buildHalfYearlyCustomDueDateDialog(
                    currentValues.rule
                )}
            />
        );
    };
}

const defaultWeeklyRule: WeeklyRecurrenceRule = {
    days: [],
    periodicity: 1,
};

const defaultWeeklyRuleWithArrays: WithArrays<WeeklyRecurrenceRule> = {
    days: [[]],
    periodicity: [1],
};

const defaultDefaultDueDateOffsetWithArrays: MonthDueDateOffsetWithArrays = {
    offsetPeriod: {
        months: [0],
        days: [0],
    },
    anchor: ["EndOfInterval"],
    offsetType: ["AfterAnchor"],
};

const defaultCalendarId: number | null = null;

const defaultMonthlyRule: MonthlyRecurrenceRule = {
    occurrences: {
        January: "Default",
        February: "Default",
        March: "Default",
        April: "Default",
        May: "Default",
        June: "Default",
        July: "Default",
        August: "Default",
        September: "Default",
        October: "Default",
        November: "Default",
        December: "Default",
    },
    calendarId: defaultCalendarId,
    defaultDueDateOffset: DEFAULT_DUE_DATE_OFFSET,
};

function generateDefaultMonthlyRuleWithArrays(
    count: number
): MonthlyRecurrenceRuleWithArrays {
    return {
        occurrences: {
            January: Array(count).fill("Default"),
            February: Array(count).fill("Default"),
            March: Array(count).fill("Default"),
            April: Array(count).fill("Default"),
            May: Array(count).fill("Default"),
            June: Array(count).fill("Default"),
            July: Array(count).fill("Default"),
            August: Array(count).fill("Default"),
            September: Array(count).fill("Default"),
            October: Array(count).fill("Default"),
            November: Array(count).fill("Default"),
            December: Array(count).fill("Default"),
        },
        calendarId: [defaultCalendarId],
        defaultDueDateOffset: defaultDefaultDueDateOffsetWithArrays,
    };
}

const defaultEveryTwoMonthsRule: EveryTwoMonthsRecurrenceRule = {
    occurrences: {
        JanuaryFebruary: "Default",
        MarchApril: "Default",
        MayJune: "Default",
        JulyAugust: "Default",
        SeptemberOctober: "Default",
        NovemberDecember: "Default",
    },
    calendarId: defaultCalendarId,
    defaultDueDateOffset: DEFAULT_DUE_DATE_OFFSET,
};

function generateDefaultEveryTwoMonthsRuleWithArrays(
    count: number
): EveryTwoMonthsRecurrenceRuleWithArrays {
    return {
        occurrences: {
            JanuaryFebruary: Array(count).fill("Default"),
            MarchApril: Array(count).fill("Default"),
            MayJune: Array(count).fill("Default"),
            JulyAugust: Array(count).fill("Default"),
            SeptemberOctober: Array(count).fill("Default"),
            NovemberDecember: Array(count).fill("Default"),
        },
        calendarId: [defaultCalendarId],
        defaultDueDateOffset: defaultDefaultDueDateOffsetWithArrays,
    };
}

const defaultQuarterlyRule: QuarterlyRecurrenceRule = {
    occurrences: {
        First: "Default",
        Second: "Default",
        Third: "Default",
        Fourth: "Default",
    },
    calendarId: null,
    financialYearId: null,
    defaultDueDateOffset: DEFAULT_DUE_DATE_OFFSET,
};

function generateDefaultQuarterlyRuleWithArrays(
    count: number
): QuarterlyRecurrenceRuleWithArrays {
    return {
        occurrences: {
            First: Array(count).fill("Default"),
            Second: Array(count).fill("Default"),
            Third: Array(count).fill("Default"),
            Fourth: Array(count).fill("Default"),
        },
        calendarId: [null],
        financialYearId: [null],
        defaultDueDateOffset: defaultDefaultDueDateOffsetWithArrays,
    };
}

const defaultHalfYearlyRule: HalfYearlyRecurrenceRule = {
    occurrences: {
        First: "Default",
        Second: "Default",
    },
    calendarId: null,
    financialYearId: null,
    defaultDueDateOffset: DEFAULT_DUE_DATE_OFFSET,
};

function generateDefaultHalfYearlyRuleWithArrays(
    count: number
): HalfYearlyRecurrenceRuleWithArrays {
    return {
        occurrences: {
            First: Array(count).fill("Default"),
            Second: Array(count).fill("Default"),
        },
        calendarId: [null],
        financialYearId: [null],
        defaultDueDateOffset: defaultDefaultDueDateOffsetWithArrays,
    };
}

const defaultYearlyRule: YearlyRecurrenceRule = {
    periodicity: 1,
    calendarId: null,
    financialYearId: null,
    defaultDueDateOffset: DEFAULT_DUE_DATE_OFFSET,
};

const defaultYearlyRuleWithArrays: YearlyRecurrenceRuleWithArrays = {
    periodicity: [1],
    calendarId: [null],
    financialYearId: [null],
    defaultDueDateOffset: defaultDefaultDueDateOffsetWithArrays,
};

class RuleForm extends React.PureComponent<RuleProps> {
    render = () => {
        const { buildStartingDateSelector, type, recurenceRule } = this.props;

        return (
            <>
                <div className={theme.alignIntervalStartDate}>
                    {this.buildRuleSelector()}
                    {recurenceRule.type !== "Date"
                        ? buildStartingDateSelector(type)
                        : null}
                    {this.props.buildPeriodWidget(type)}
                </div>
                {this.buildRule()}
            </>
        );
    };

    private buildRule = () => {
        const {
            currentRule,
            recurenceRule,
            buildDefaultDueDateOffsetInput,
            buildManualDueDateAndTimeInputs,
            buildMonthlyCustomDueDateDialog,
            onChangeMonthOccurrence,
            buildTwoMonthsCustomDueDateDialog,
            onChangeTwoMonthsOccurrence,
            buildQuarterlyCustomDueDateDialog,
            onChangeQuarterlyOccurrence,
            buildHalfYearlyCustomDueDateDialog,
            onChangeHalfYearlyOccurrence,
            buildResponsibleAllocatedTimeInputs,
            buildReviewerAllocatedTimeInputs,
            buildRemindersWidget,
            onChangePeriodicity,
            onChangeFullWeekdays,
            onChangePartialWeekdays,
            financialYear,
            onChangeFinancialYear,
            getFieldError,
            errors,
        } = this.props;
        switch (recurenceRule.type) {
            case "Date": {
                switch (this.props.type) {
                    case "EDIT":
                        return (
                            <ManualRecurrenceRuleUpdater
                                buildDueDateAndTimeInputs={
                                    buildManualDueDateAndTimeInputs
                                }
                                buildResponsibleAllocatedTimeInputs={
                                    buildResponsibleAllocatedTimeInputs
                                }
                                buildReviewerAllocatedTimeInputs={
                                    buildReviewerAllocatedTimeInputs
                                }
                                buildRemindersWidget={buildRemindersWidget}
                            />
                        );
                    case "READ_ONLY":
                        return (
                            <ManualRecurrenceRuleViewer
                                buildDueDateAndTimeInputs={
                                    buildManualDueDateAndTimeInputs
                                }
                                buildResponsibleAllocatedTimeInputs={
                                    buildResponsibleAllocatedTimeInputs
                                }
                                buildReviewerAllocatedTimeInputs={
                                    buildReviewerAllocatedTimeInputs
                                }
                                buildRemindersWidget={buildRemindersWidget}
                            />
                        );
                    default:
                        return assertNever(this.props);
                }
            }
            case "Weekly":
                if (currentRule.type === "Weekly") {
                    switch (recurenceRule.kind) {
                        case "Full":
                            return (
                                <FullWeeklyRecurrenceRuleUpdater
                                    currentRule={currentRule.data}
                                    onChangePeriodicity={onChangePeriodicity}
                                    onChangeFullWeekdays={onChangeFullWeekdays}
                                    buildResponsibleAllocatedTimeInputs={
                                        buildResponsibleAllocatedTimeInputs
                                    }
                                    buildReviewerAllocatedTimeInputs={
                                        buildReviewerAllocatedTimeInputs
                                    }
                                    buildRemindersWidget={buildRemindersWidget}
                                    getFieldError={getFieldError}
                                    errors={errors}
                                />
                            );
                        case "Partial":
                            switch (this.props.type) {
                                case "EDIT":
                                    return (
                                        <PartialWeeklyRecurrenceRuleUpdater
                                            errors={errors}
                                            getFieldError={getFieldError}
                                            currentRule={currentRule.data}
                                            onChangePeriodicity={
                                                onChangePeriodicity
                                            }
                                            onChangePartialWeekdays={
                                                onChangePartialWeekdays
                                            }
                                            buildResponsibleAllocatedTimeInputs={
                                                buildResponsibleAllocatedTimeInputs
                                            }
                                            buildReviewerAllocatedTimeInputs={
                                                buildReviewerAllocatedTimeInputs
                                            }
                                            buildRemindersWidget={
                                                buildRemindersWidget
                                            }
                                        />
                                    );
                                case "READ_ONLY":
                                    return (
                                        <PartialWeeklyRecurrenceRuleViewer
                                            currentRule={currentRule.data}
                                            buildResponsibleAllocatedTimeInputs={
                                                buildResponsibleAllocatedTimeInputs
                                            }
                                            buildReviewerAllocatedTimeInputs={
                                                buildReviewerAllocatedTimeInputs
                                            }
                                            buildRemindersWidget={
                                                buildRemindersWidget
                                            }
                                        />
                                    );
                                default:
                                    return assertNever(this.props);
                            }
                        default:
                            return assertNever(recurenceRule);
                    }
                }
                return <></>;
            case "Monthly":
                if (currentRule.type === "Monthly") {
                    switch (recurenceRule.kind) {
                        case "Full":
                            return (
                                <FullMonthlyRecurrenceRuleUpdater
                                    currentRule={currentRule.data}
                                    buildDefaultDueDateOffsetInput={
                                        buildDefaultDueDateOffsetInput
                                    }
                                    buildResponsibleAllocatedTimeInputs={
                                        buildResponsibleAllocatedTimeInputs
                                    }
                                    buildReviewerAllocatedTimeInputs={
                                        buildReviewerAllocatedTimeInputs
                                    }
                                    buildCustomDueDateDialog={
                                        buildMonthlyCustomDueDateDialog
                                    }
                                    buildRemindersWidget={buildRemindersWidget}
                                    onChangeMonthOccurrence={
                                        onChangeMonthOccurrence
                                    }
                                />
                            );
                        case "Partial":
                            switch (this.props.type) {
                                case "EDIT":
                                    return (
                                        <PartialMonthlyRecurrenceRuleUpdater
                                            currentRule={currentRule.data}
                                            buildDefaultDueDateOffsetInput={
                                                buildDefaultDueDateOffsetInput
                                            }
                                            buildResponsibleAllocatedTimeInputs={
                                                buildResponsibleAllocatedTimeInputs
                                            }
                                            buildReviewerAllocatedTimeInputs={
                                                buildReviewerAllocatedTimeInputs
                                            }
                                            buildCustomDueDateDialog={
                                                buildMonthlyCustomDueDateDialog
                                            }
                                            buildRemindersWidget={
                                                buildRemindersWidget
                                            }
                                            onChangeMonthOccurrence={
                                                onChangeMonthOccurrence
                                            }
                                        />
                                    );
                                case "READ_ONLY":
                                    return (
                                        <PartialMonthlyRecurrenceRuleViewer
                                            currentRule={currentRule.data}
                                            buildDefaultDueDateOffsetInput={
                                                buildDefaultDueDateOffsetInput
                                            }
                                            buildResponsibleAllocatedTimeInputs={
                                                buildResponsibleAllocatedTimeInputs
                                            }
                                            buildReviewerAllocatedTimeInputs={
                                                buildReviewerAllocatedTimeInputs
                                            }
                                            buildCustomDueDateDialog={
                                                buildMonthlyCustomDueDateDialog
                                            }
                                            buildRemindersWidget={
                                                buildRemindersWidget
                                            }
                                        />
                                    );
                                default:
                                    return assertNever(this.props);
                            }
                        default:
                            return assertNever(recurenceRule);
                    }
                }
                return <></>;
            case "EveryTwoMonths":
                if (currentRule.type === "EveryTwoMonths") {
                    switch (recurenceRule.kind) {
                        case "Full":
                            return (
                                <FullEveryTwoMonthsRecurrenceRuleUpdater
                                    currentRule={currentRule.data}
                                    buildDefaultDueDateOffsetInput={
                                        buildDefaultDueDateOffsetInput
                                    }
                                    buildResponsibleAllocatedTimeInputs={
                                        buildResponsibleAllocatedTimeInputs
                                    }
                                    buildReviewerAllocatedTimeInputs={
                                        buildReviewerAllocatedTimeInputs
                                    }
                                    buildCustomDueDateDialog={
                                        buildTwoMonthsCustomDueDateDialog
                                    }
                                    buildRemindersWidget={buildRemindersWidget}
                                    onChangeTwoMonthsOccurrence={
                                        onChangeTwoMonthsOccurrence
                                    }
                                />
                            );
                        case "Partial":
                            switch (this.props.type) {
                                case "EDIT":
                                    return (
                                        <PartialEveryTwoMonthsRecurrenceRuleUpdater
                                            currentRule={currentRule.data}
                                            buildDefaultDueDateOffsetInput={
                                                buildDefaultDueDateOffsetInput
                                            }
                                            buildResponsibleAllocatedTimeInputs={
                                                buildResponsibleAllocatedTimeInputs
                                            }
                                            buildReviewerAllocatedTimeInputs={
                                                buildReviewerAllocatedTimeInputs
                                            }
                                            buildCustomDueDateDialog={
                                                buildTwoMonthsCustomDueDateDialog
                                            }
                                            buildRemindersWidget={
                                                buildRemindersWidget
                                            }
                                            onChangeTwoMonthsOccurrence={
                                                onChangeTwoMonthsOccurrence
                                            }
                                        />
                                    );
                                case "READ_ONLY":
                                    return (
                                        <PartialEveryTwoMonthsRecurrenceRuleViewer
                                            currentRule={currentRule.data}
                                            buildDefaultDueDateOffsetInput={
                                                buildDefaultDueDateOffsetInput
                                            }
                                            buildResponsibleAllocatedTimeInputs={
                                                buildResponsibleAllocatedTimeInputs
                                            }
                                            buildReviewerAllocatedTimeInputs={
                                                buildReviewerAllocatedTimeInputs
                                            }
                                            buildCustomDueDateDialog={
                                                buildTwoMonthsCustomDueDateDialog
                                            }
                                            buildRemindersWidget={
                                                buildRemindersWidget
                                            }
                                        />
                                    );
                                default:
                                    return assertNever(this.props);
                            }
                        default:
                            return assertNever(recurenceRule);
                    }
                }
                return <></>;
            case "Quarterly":
                if (currentRule.type === "Quarterly") {
                    switch (recurenceRule.kind) {
                        case "Full":
                            return (
                                <FullQuarterlyRecurrenceRuleUpdater
                                    financialYear={financialYear}
                                    currentRule={currentRule.data}
                                    buildDefaultDueDateOffsetInput={
                                        buildDefaultDueDateOffsetInput
                                    }
                                    buildResponsibleAllocatedTimeInputs={
                                        buildResponsibleAllocatedTimeInputs
                                    }
                                    buildReviewerAllocatedTimeInputs={
                                        buildReviewerAllocatedTimeInputs
                                    }
                                    buildCustomDueDateDialog={
                                        buildQuarterlyCustomDueDateDialog
                                    }
                                    buildRemindersWidget={buildRemindersWidget}
                                    onChangeFinancialYear={
                                        onChangeFinancialYear
                                    }
                                    onChangeQuarterlyOccurrence={
                                        onChangeQuarterlyOccurrence
                                    }
                                />
                            );
                        case "Partial":
                            switch (this.props.type) {
                                case "EDIT":
                                    return (
                                        <PartialQuarterlyRecurrenceRuleUpdater
                                            financialYear={financialYear}
                                            currentRule={currentRule.data}
                                            buildDefaultDueDateOffsetInput={
                                                buildDefaultDueDateOffsetInput
                                            }
                                            buildResponsibleAllocatedTimeInputs={
                                                buildResponsibleAllocatedTimeInputs
                                            }
                                            buildReviewerAllocatedTimeInputs={
                                                buildReviewerAllocatedTimeInputs
                                            }
                                            buildCustomDueDateDialog={
                                                buildQuarterlyCustomDueDateDialog
                                            }
                                            buildRemindersWidget={
                                                buildRemindersWidget
                                            }
                                            onChangeFinancialYear={
                                                onChangeFinancialYear
                                            }
                                            onChangeQuarterlyOccurrence={
                                                onChangeQuarterlyOccurrence
                                            }
                                        />
                                    );
                                case "READ_ONLY":
                                    return (
                                        <PartialQuarterlyRecurrenceRuleViewer
                                            financialYear={financialYear}
                                            currentRule={currentRule.data}
                                            buildDefaultDueDateOffsetInput={
                                                buildDefaultDueDateOffsetInput
                                            }
                                            buildResponsibleAllocatedTimeInputs={
                                                buildResponsibleAllocatedTimeInputs
                                            }
                                            buildReviewerAllocatedTimeInputs={
                                                buildReviewerAllocatedTimeInputs
                                            }
                                            buildCustomDueDateDialog={
                                                buildQuarterlyCustomDueDateDialog
                                            }
                                            buildRemindersWidget={
                                                buildRemindersWidget
                                            }
                                        />
                                    );
                                default:
                                    return assertNever(this.props);
                            }
                        default:
                            return assertNever(recurenceRule);
                    }
                }
                return <></>;
            case "HalfYearly":
                if (currentRule.type === "HalfYearly") {
                    switch (recurenceRule.kind) {
                        case "Full":
                            return (
                                <FullHalfYearlyRecurrenceRuleUpdater
                                    financialYear={financialYear}
                                    currentRule={currentRule.data}
                                    buildDefaultDueDateOffsetInput={
                                        buildDefaultDueDateOffsetInput
                                    }
                                    buildResponsibleAllocatedTimeInputs={
                                        buildResponsibleAllocatedTimeInputs
                                    }
                                    buildReviewerAllocatedTimeInputs={
                                        buildReviewerAllocatedTimeInputs
                                    }
                                    buildCustomDueDateDialog={
                                        buildHalfYearlyCustomDueDateDialog
                                    }
                                    buildRemindersWidget={buildRemindersWidget}
                                    onChangeFinancialYear={
                                        onChangeFinancialYear
                                    }
                                    onChangeHalfYearlyOccurrence={
                                        onChangeHalfYearlyOccurrence
                                    }
                                />
                            );
                        case "Partial":
                            switch (this.props.type) {
                                case "EDIT":
                                    return (
                                        <PartialHalfYearlyRecurrenceRuleUpdater
                                            financialYear={financialYear}
                                            currentRule={currentRule.data}
                                            buildDefaultDueDateOffsetInput={
                                                buildDefaultDueDateOffsetInput
                                            }
                                            buildResponsibleAllocatedTimeInputs={
                                                buildResponsibleAllocatedTimeInputs
                                            }
                                            buildReviewerAllocatedTimeInputs={
                                                buildReviewerAllocatedTimeInputs
                                            }
                                            buildRemindersWidget={
                                                buildRemindersWidget
                                            }
                                            buildCustomDueDateDialog={
                                                buildHalfYearlyCustomDueDateDialog
                                            }
                                            onChangeFinancialYear={
                                                onChangeFinancialYear
                                            }
                                            onChangeHalfYearlyOccurrence={
                                                onChangeHalfYearlyOccurrence
                                            }
                                        />
                                    );
                                case "READ_ONLY":
                                    return (
                                        <PartialHalfYearlyRecurrenceRuleViewer
                                            financialYear={financialYear}
                                            currentRule={currentRule.data}
                                            buildDefaultDueDateOffsetInput={
                                                buildDefaultDueDateOffsetInput
                                            }
                                            buildResponsibleAllocatedTimeInputs={
                                                buildResponsibleAllocatedTimeInputs
                                            }
                                            buildReviewerAllocatedTimeInputs={
                                                buildReviewerAllocatedTimeInputs
                                            }
                                            buildCustomDueDateDialog={
                                                buildHalfYearlyCustomDueDateDialog
                                            }
                                            buildRemindersWidget={
                                                buildRemindersWidget
                                            }
                                        />
                                    );
                                default:
                                    return assertNever(this.props);
                            }
                        default:
                            return assertNever(recurenceRule);
                    }
                }
                return <></>;
            case "Yearly":
                if (currentRule.type === "Yearly") {
                    switch (recurenceRule.kind) {
                        case "Full":
                            return (
                                <FullYearlyRecurrenceRuleUpdater
                                    financialYear={financialYear}
                                    currentRule={currentRule.data}
                                    buildDefaultDueDateOffsetInput={
                                        buildDefaultDueDateOffsetInput
                                    }
                                    buildResponsibleAllocatedTimeInputs={
                                        buildResponsibleAllocatedTimeInputs
                                    }
                                    buildReviewerAllocatedTimeInputs={
                                        buildReviewerAllocatedTimeInputs
                                    }
                                    buildRemindersWidget={buildRemindersWidget}
                                    onChangeFinancialYear={
                                        onChangeFinancialYear
                                    }
                                />
                            );
                        case "Partial":
                            switch (this.props.type) {
                                case "EDIT":
                                    return (
                                        <PartialYearlyRecurrenceRuleUpdater
                                            financialYear={financialYear}
                                            currentRule={currentRule.data}
                                            buildDefaultDueDateOffsetInput={
                                                buildDefaultDueDateOffsetInput
                                            }
                                            buildResponsibleAllocatedTimeInputs={
                                                buildResponsibleAllocatedTimeInputs
                                            }
                                            buildReviewerAllocatedTimeInputs={
                                                buildReviewerAllocatedTimeInputs
                                            }
                                            buildRemindersWidget={
                                                buildRemindersWidget
                                            }
                                            onChangeFinancialYear={
                                                onChangeFinancialYear
                                            }
                                        />
                                    );
                                case "READ_ONLY":
                                    return (
                                        <PartialYearlyRecurrenceRuleViewer
                                            financialYear={financialYear}
                                            currentRule={currentRule.data}
                                            buildDefaultDueDateOffsetInput={
                                                buildDefaultDueDateOffsetInput
                                            }
                                            buildResponsibleAllocatedTimeInputs={
                                                buildResponsibleAllocatedTimeInputs
                                            }
                                            buildReviewerAllocatedTimeInputs={
                                                buildReviewerAllocatedTimeInputs
                                            }
                                            buildRemindersWidget={
                                                buildRemindersWidget
                                            }
                                        />
                                    );
                                default:
                                    return assertNever(this.props);
                            }
                        default:
                            return assertNever(recurenceRule);
                    }
                }
                return <></>;
            default:
                return assertNever(recurenceRule);
        }
    };

    private buildRuleSelector = () => {
        const { recurenceRule } = this.props;
        const schedulingType: SchedulingType = normalizeSchedulingType(
            recurenceRule.type
        );
        switch (this.props.type) {
            case "EDIT":
                return (
                    <SchedulingSelect
                        value={schedulingType}
                        type="EDIT"
                        onChange={this.props.onChangeSchedulingType}
                    />
                );
            case "READ_ONLY":
                return (
                    <SchedulingSelect value={schedulingType} type="READ_ONLY" />
                );
            default:
                return assertNever(this.props);
        }
    };
}

interface BasicRuleProps {
    financialYear: FinancialYearView | null;
    currentRule: RecurrenceRuleWithArrays;
    recurenceRule: UpdatableControlRecurrenceRule;
    onChangeFinancialYear: (financialYearId: number | null) => void;
    onChangePeriodicity: (periodicity: number) => void;
    onChangePartialWeekdays: (day: DayOfWeek, value: boolean) => void;
    onChangeFullWeekdays: (day: DayOfWeek, value: boolean) => void;
    buildPeriodWidget: (type: "EDIT" | "READ_ONLY") => React.ReactNode;
    buildStartingDateSelector: (type: "EDIT" | "READ_ONLY") => React.ReactNode;
    buildRemindersWidget: (type: "EDIT" | "READ_ONLY") => React.ReactNode;
    buildResponsibleAllocatedTimeInputs: (
        type: "EDIT" | "READ_ONLY"
    ) => React.ReactNode;
    buildReviewerAllocatedTimeInputs: (
        type: "EDIT" | "READ_ONLY",
        subsequentStepIndex: SubsequentStepIndex
    ) => React.ReactNode;
    buildDefaultDueDateOffsetInput: (
        type: "EDIT" | "READ_ONLY",
        period: PeriodType
    ) => React.ReactNode;
    buildManualDueDateAndTimeInputs: (
        type: "EDIT" | "READ_ONLY"
    ) => React.ReactNode;
    buildMonthlyCustomDueDateDialog: (
        type: "EDIT" | "READ_ONLY"
    ) => React.ReactNode;
    buildTwoMonthsCustomDueDateDialog: (
        type: "EDIT" | "READ_ONLY"
    ) => React.ReactNode;
    buildQuarterlyCustomDueDateDialog: (
        type: "EDIT" | "READ_ONLY"
    ) => React.ReactNode;
    buildHalfYearlyCustomDueDateDialog: (
        type: "EDIT" | "READ_ONLY"
    ) => React.ReactNode;
    onChangeSchedulingType: (schedulingType: SchedulingType) => void;
    getFieldError: (
        errors: Partial<ErrorMap<ErrorKey>>,
        key: ErrorKey
    ) => ErrorValue;
    errors: Partial<ErrorMap<ErrorKey>>;
    onChangeMonthOccurrence: (
        occurrence: Month,
        value: SchedulingOccurrenceChange | undefined
    ) => void;
    onChangeTwoMonthsOccurrence: (
        occurrence: TwoMonths,
        value: SchedulingOccurrenceChange | undefined
    ) => void;
    onChangeQuarterlyOccurrence: (
        occurrence: Quarter,
        value: SchedulingOccurrenceChange | undefined
    ) => void;
    onChangeHalfYearlyOccurrence: (
        occurrence: HalfYear,
        value: SchedulingOccurrenceChange | undefined
    ) => void;
}

type RuleProps = BasicRuleProps & ({ type: "READ_ONLY" } | { type: "EDIT" });

interface SchedulingSelectInputOption {
    value: SchedulingType;
    label: string;
}

class SchedulingSelect extends React.PureComponent<SchedulingSelectProps> {
    onChangeInternal = (value: string | undefined): void => {
        switch (this.props.type) {
            case "EDIT":
                const { onChange } = this.props;
                let newSchedulingType: SchedulingType;
                switch (value) {
                    case "Weekly":
                        newSchedulingType = "Weekly";
                        break;
                    case "Monthly":
                        newSchedulingType = "Monthly";
                        break;
                    case "EveryTwoMonths":
                        newSchedulingType = "EveryTwoMonths";
                        break;
                    case "Quarterly":
                        newSchedulingType = "Quarterly";
                        break;
                    case "HalfYearly":
                        newSchedulingType = "HalfYearly";
                        break;
                    case "Yearly":
                        newSchedulingType = "Yearly";
                        break;
                    case "Manual":
                        newSchedulingType = "Manual";
                        break;
                    default:
                        throw new Error(`Invalid scheduling type ${value}`);
                }
                onChange(newSchedulingType);
                break;
            case "READ_ONLY":
                throw new Error("No onChange in read-only mode");
            default:
                return assertNever(this.props);
        }
    };

    render = () => {
        const { value } = this.props;

        const options: SchedulingSelectInputOption[] = [
            {
                value: "Weekly",
                label: t("CreateControl_Recurrence_Interval_Weekly"),
            },
            {
                value: "Monthly",
                label: t("CreateControl_Recurrence_Interval_Monthly"),
            },
            {
                value: "EveryTwoMonths",
                label: t("CreateControl_Recurrence_Interval_Every2Months"),
            },
            {
                value: "Quarterly",
                label: t("CreateControl_Recurrence_Interval_Quarterly"),
            },
            {
                value: "HalfYearly",
                label: t("CreateControl_Recurrence_Interval_HalfYearly"),
            },
            {
                value: "Yearly",
                label: t("CreateControl_Recurrence_Interval_Yearly"),
            },
            { value: "Manual", label: t("CreateControl_Recurrence_Manual") },
        ];
        const baseProps: SelectInputCommonProps = {
            label: t("CreateControl_Recurrence_Interval"),
            value,
            options,
        };
        let props: SelectInputProps<"recurrence-type-select", object>;
        switch (this.props.type) {
            case "EDIT":
                props = {
                    ...baseProps,
                    id: "recurrence-type-select",
                    type: "EDIT",
                    onChange: this.onChangeInternal,
                };
                break;
            case "READ_ONLY":
                props = { ...baseProps, type: "READ_ONLY" };
                break;
            default:
                return assertNever(this.props);
        }
        return (
            <SelectInput {...props} className={theme.widerSchedulingSelect} />
        );
    };
}

type SchedulingSelectProps = { value: SchedulingType } & (
    | { type: "READ_ONLY" }
    | { type: "EDIT"; onChange: (newSchedulingType: SchedulingType) => void }
);

function normalizeSchedulingType(
    schedulingType: SchedulingType | "Date"
): SchedulingType {
    switch (schedulingType) {
        case "Date":
            return "Manual";
        default:
            return schedulingType;
    }
}

const applyWeekdayChangesToCurrentValues = (
    currentDays: DayOfWeek[],
    day: DayOfWeek,
    value: boolean
): DayOfWeek[] => {
    let newDays = currentDays.slice();

    if (value) {
        if (!newDays.includes(day)) {
            newDays.push(day);
        }
    } else {
        newDays = newDays.filter((d) => d !== day);
    }

    return newDays;
};

const applyWeekdayChangesToPatch = (
    oldDays: Partial<Record<DayOfWeek, Presence>> | undefined,
    day: DayOfWeek,
    value: boolean
): Partial<Record<DayOfWeek, Presence>> => {
    if (oldDays === undefined) {
        return {
            [day]: booleanToPresence(value),
        };
    } else {
        return {
            ...oldDays,
            [day]: booleanToPresence(value),
        };
    }
};

function booleanToPresence(value: boolean): Presence {
    return value ? "Present" : "Absent";
}

function addMonthlyOccurrences(
    currentOccurrences: GenericOccurrencesArray<Month>,
    newOccurrences: Partial<Record<Month, DefaultOrCustomOffset>>
): GenericOccurrencesArray<Month> {
    return {
        January: [...currentOccurrences.January, newOccurrences.January],
        February: [...currentOccurrences.February, newOccurrences.February],
        March: [...currentOccurrences.March, newOccurrences.March],
        April: [...currentOccurrences.April, newOccurrences.April],
        May: [...currentOccurrences.May, newOccurrences.May],
        June: [...currentOccurrences.June, newOccurrences.June],
        July: [...currentOccurrences.July, newOccurrences.July],
        August: [...currentOccurrences.August, newOccurrences.August],
        September: [...currentOccurrences.September, newOccurrences.September],
        October: [...currentOccurrences.October, newOccurrences.October],
        November: [...currentOccurrences.November, newOccurrences.November],
        December: [...currentOccurrences.December, newOccurrences.December],
    };
}

function addTwoMonthsOccurrences(
    currentOccurrences: GenericOccurrencesArray<TwoMonths>,
    newOccurrences: Partial<Record<TwoMonths, DefaultOrCustomOffset>>
): GenericOccurrencesArray<TwoMonths> {
    return {
        JanuaryFebruary: [
            ...currentOccurrences.JanuaryFebruary,
            newOccurrences.JanuaryFebruary,
        ],
        MarchApril: [
            ...currentOccurrences.MarchApril,
            newOccurrences.MarchApril,
        ],
        MayJune: [...currentOccurrences.MayJune, newOccurrences.MayJune],
        JulyAugust: [
            ...currentOccurrences.JulyAugust,
            newOccurrences.JulyAugust,
        ],
        SeptemberOctober: [
            ...currentOccurrences.SeptemberOctober,
            newOccurrences.SeptemberOctober,
        ],
        NovemberDecember: [
            ...currentOccurrences.NovemberDecember,
            newOccurrences.NovemberDecember,
        ],
    };
}

function addQuarterlyOccurrences(
    currentOccurrences: GenericOccurrencesArray<Quarter>,
    newOccurrences: Partial<Record<Quarter, DefaultOrCustomOffset>>
): GenericOccurrencesArray<Quarter> {
    return {
        First: [...currentOccurrences.First, newOccurrences.First],
        Second: [...currentOccurrences.Second, newOccurrences.Second],
        Third: [...currentOccurrences.Third, newOccurrences.Third],
        Fourth: [...currentOccurrences.Fourth, newOccurrences.Fourth],
    };
}

function addHalfYearlyOccurrences(
    currentOccurrences: GenericOccurrencesArray<HalfYear>,
    newOccurrences: Partial<Record<HalfYear, DefaultOrCustomOffset>>
): GenericOccurrencesArray<HalfYear> {
    return {
        First: [...currentOccurrences.First, newOccurrences.First],
        Second: [...currentOccurrences.Second, newOccurrences.Second],
    };
}

export type {
    SchedulingSelectProps,
    UpdatableControlSchedulingForm,
    UpdatableControlSchedulingCurrentValues,
};
export { ModifySchedulingForm, SchedulingSelect };
